/**
 * Spec 007 — donations (one-off Stripe, §7) fetch wrappers. Same discriminated-union contract as
 * lib/finance.ts: these NEVER throw; each returns an explicit outcome the UI renders across the four
 * states. Money is integer öre. The public create + status poll send no credentials that matter (the
 * create is public); the board calls send cookies + the double-submit CSRF token. A `503` (provider
 * unconfigured) surfaces as a distinct `unavailable` outcome so the UI can say so specifically.
 */
import { readCsrfToken } from './auth';

const TIMEOUT_MS = 10_000;

export type DonationStatus =
  | 'pending'
  | 'paid'
  | 'awaiting_posting'
  | 'failed'
  | 'cancelled'
  | 'refunded';

export type Result<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'invalid'; errors: Record<string, string>; message?: string }
  | { kind: 'unavailable'; message: string }
  | { kind: 'conflict'; message: string }
  | { kind: 'forbidden'; message: string }
  | { kind: 'notfound' }
  | { kind: 'error' };

export interface CreateDonationResult {
  redirectUrl: string;
  providerRef: string;
}

export interface DonationStatusView {
  status: DonationStatus;
  amountOre: number;
}

export interface BoardDonation {
  id: string;
  status: DonationStatus;
  amountOre: number;
  currency: string;
  donor: string | null;
  providerRef: string;
  ledgerEntryId: string | null;
  createdAt: string;
}

async function req(method: string, path: string, body?: unknown): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }
  try {
    return await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function toResult<T>(res: Response): Promise<Result<T>> {
  if (res.ok) return { kind: 'ok', data: (await res.json().catch(() => ({}))) as T };
  const body = (await res.json().catch(() => ({}))) as {
    message?: string | string[];
    errors?: Record<string, string>;
  };
  const firstMessage = Array.isArray(body.message) ? body.message[0] : body.message;
  if (res.status === 400) return { kind: 'invalid', errors: body.errors ?? {}, message: firstMessage };
  if (res.status === 403) return { kind: 'forbidden', message: firstMessage ?? 'You don’t have permission to do that.' };
  if (res.status === 404) return { kind: 'notfound' };
  if (res.status === 409) return { kind: 'conflict', message: firstMessage ?? 'That action isn’t possible right now.' };
  if (res.status === 429) return { kind: 'unavailable', message: 'Too many attempts. Please wait a moment and try again.' };
  if (res.status === 503) return { kind: 'unavailable', message: firstMessage ?? 'Donations aren’t available right now.' };
  return { kind: 'error' };
}

const call = async <T>(method: string, path: string, body?: unknown): Promise<Result<T>> => {
  try {
    return await toResult<T>(await req(method, path, body));
  } catch {
    return { kind: 'error' };
  }
};

export interface CreateDonationInput {
  amountOre: number;
  donorName?: string;
  donorEmail?: string;
}

export const createDonation = (b: CreateDonationInput) =>
  call<CreateDonationResult>('POST', '/api/donations', b);

export const getDonationStatus = (providerRef: string) =>
  call<DonationStatusView>('GET', `/api/donations/status/${encodeURIComponent(providerRef)}`);

export const listDonations = () => call<BoardDonation[]>('GET', '/api/donations');

export const postAwaitingDonation = (id: string) =>
  call<BoardDonation>('POST', `/api/donations/${id}/post`);

// ---- Spec 007c — recurring (monthly Stripe subscription) donations ----

export type RecurringStatus =
  | 'pending'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'incomplete_expired';

/** Board row for a recurring donation. Donor email is intentionally absent (data minimisation). */
export interface BoardRecurringDonation {
  id: string;
  status: RecurringStatus;
  amountOre: number;
  currency: string;
  donor: string | null;
  renewalCount: number;
  subscriptionRef: string | null;
  createdAt: string;
  cancelledAt: string | null;
}

/** Whether the bound provider supports recurring — drives showing the monthly option on /donate. */
export const recurringAvailable = () =>
  call<{ available: boolean }>('GET', '/api/donations/recurring/available');

/** Start a monthly recurring donation → hosted subscription checkout (same redirect contract as one-off). */
export const startRecurring = (b: CreateDonationInput) =>
  call<CreateDonationResult>('POST', '/api/donations/recurring', b);

/** Board list of recurring donations (no donor email). */
export const listRecurringDonations = () =>
  call<BoardRecurringDonation[]>('GET', '/api/donations/recurring');

/** Public cancel via the emailed single-purpose manage token (no login). */
export const cancelRecurringByToken = (token: string) =>
  call<{ status: string }>('POST', `/api/donations/recurring/cancel/${encodeURIComponent(token)}`);

/** Member cancels their OWN recurring donation (sends the CSRF token via req()). */
export const cancelRecurringByMember = (id: string) =>
  call<{ status: string }>('POST', `/api/donations/recurring/${encodeURIComponent(id)}/cancel`);

// ---- öre formatting (presentation only; never used in arithmetic) — mirrors lib/finance.ts ----
export function formatOre(ore: number): string {
  const negative = ore < 0;
  const abs = Math.abs(ore);
  const kronor = Math.floor(abs / 100);
  const rest = abs % 100;
  const grouped = kronor.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${negative ? '-' : ''}${grouped},${rest.toString().padStart(2, '0')} €`;
}

/** Parse a "123,45" / "123.45" EUR string to integer minor units (cents); null if not a clean amount. */
export function parseSekToOre(input: string): number | null {
  const trimmed = input.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [kr, dec = ''] = trimmed.split('.');
  const ore = Number(kr) * 100 + Number(dec.padEnd(2, '0'));
  return Number.isSafeInteger(ore) ? ore : null;
}
